/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module TechSupportListPageModule
 */

import {
    AfterViewInit,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';

import { Subscription } from 'rxjs';
import { ClrWizard, ClrWizardPage } from '@clr/angular';

import { ITechSupport, PreCheckFsmState } from 'generated-types';
import { L10nService } from '@vmw/ngx-vip';
import * as globalL10n from 'global-l10n';
import * as l10n from './tech-support-wizard.l10n';
import { TechSupportGenerationService } from '../../../modules/support/services/tech-support-generation.service';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;
const { ...globalL10nKeys } = globalL10n;

/**
 * @description Wizard component for Tech Support to display current progress.
 * @author  Dhanashree Tilak
 */
@Component({
    selector: 'tech-support-wizard',
    templateUrl: './tech-support-wizard.component.html',
})
export class TechSupportWizardComponent implements OnChanges, AfterViewInit, OnDestroy {
    @Input()
    public selectedPackage: ITechSupport;

    /**
     * Emit on click of cancel/close button on the dialog.
     */
    @Output()
    public onClose = new EventEmitter<void>();

    /**
     * TemplateRef for wizard.
     */
    @ViewChild('techSupportWizard')
    public readonly wizard: ClrWizard;

    /**
     * TemplateRef for preCheck page.
     */
    @ViewChild('preCheckPage')
    public readonly preCheckPage: ClrWizardPage;

    /**
     * TemplateRef for Packaging page.
     */
    @ViewChild('packagingPage')
    public readonly packagingPage: ClrWizardPage;

    private packageUpdateSubscription: Subscription;

    public readonly globalL10nKeys = globalL10nKeys;

    public readonly l10nKeys = l10nKeys;

    constructor(
        private readonly l10nService: L10nService,
        private readonly techSupportGenerationService: TechSupportGenerationService,
    ) {
        l10nService.registerSourceBundles(dictionary);
    }

    /** @override */
    public ngOnChanges(changes: SimpleChanges): void {
        // We only need to act if the `selectedPackage` input has changed and the wizard is ready.
        // The wizard is only ready after `ngAfterViewInit` has been called.
        if (changes.selectedPackage && this.wizard) {
            this.updateWizardState();
        }
    }

    /** @override */
    public ngAfterViewInit(): void {
        this.updateWizardState();

        // Subscribe to package updates from the service to get live progress.
        this.packageUpdateSubscription = this.techSupportGenerationService.packagesInProgress$.subscribe(
            async(packages: ITechSupport[]) => {
                const updatedPackage = packages.find(pkg => pkg.uuid === this.selectedPackage.uuid);

                if (updatedPackage) {
                    this.selectedPackage = updatedPackage;
                    this.updateWizardState();
                } else if (this.selectedPackage && this.selectedPackage.progress < 100) {
                    // The package is no longer in the in-progress list, which means it has completed
                    // Fetch its final state to update the wizard.
                    const completedPackage = await this.techSupportGenerationService.getTechSupportPackage(
                        this.selectedPackage.uuid,
                    );

                    if (completedPackage) {
                        this.selectedPackage = completedPackage;
                        this.updateWizardState();
                    }
                }
            },
        );
    }

    /**
     * Update the wizard's state based on the selected package's progress.
     * This is called from `ngOnChanges` and `ngAfterViewInit`.
     */
    private updateWizardState(): void {
        if (this.selectedPackage && this.wizard) {
            if (this.selectedPackage.progress === 100) {
                this.packagingPage.completed = true;
            }

            const isPreCheckComplete =
                this.selectedPackage.techsupport_readiness?.state.state === PreCheckFsmState.PRECHECK_FSM_SUCCESS;

            this.preCheckPage.completed = isPreCheckComplete;

            if (isPreCheckComplete) {
                this.wizard.navService.currentPage = this.packagingPage;
            } else {
                this.wizard.navService.setLastEnabledPageCurrent();
            }
        }
    }

    /**
     * Handle wizard close.
     */
    public handleClose(): void {
        this.onClose.emit();
    }

    /**
     * Disable Next button if pre-check is in progress
     */
    public get isNextButtonDisabled(): boolean {
        return this.selectedPackage.techsupport_readiness?.progress < 100;
    }

    /** @override */
    public ngOnDestroy(): void {
        if (this.packageUpdateSubscription) {
            this.packageUpdateSubscription.unsubscribe();
        }
    }
}
